import { Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
} from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { DropdownConfig, DropdownOption } from 'src/app/components/custom-dropdown/custom-dropdown.component';
import { FaqBuilder, InfoDropdown } from 'src/app/components/faq/faq.component';
import { FeatureType } from 'src/app/components/featured-plates/featured-plates.component';
import { SidebarFilter, SidebarFilterConfig, SidebarFilterGroup } from 'src/app/components/side-bar-filters/side-bar-filters.component';
import { SECONDARY_STICKY_CONFIG, StickyConfig, StickyLink, StickyLinkGroup } from 'src/app/components/sticky-tabs/sticky-tabs.component';
import { SwitchTab } from 'src/app/components/switch-toggle/switch-toggle.component';
import { BYOTermBreakdown } from 'src/app/models/byoTermResponse';
import { Registration } from 'src/app/models/registration';
import { RegistrationService } from 'src/app/services/registration-service';
import {
  SearchReqResponse,
  SearchService,
} from 'src/app/services/search-service';
import {
  TrackingService,
  TRACKING_SEARCH_TYPE,
} from 'src/app/services/tracking-service';
import { SearchFilters } from '../search-results-page/search-results-page.component';
import GlobalFaqs from "../../global-faqs";

export class DatelessSearchResponse {
  constructor(
    public style: string,
    public layout: string,
    public main: Registration[]
  ) { }
}

export class DatelessStyleOptionGroup {
  constructor(
    public name: string,
    public viewName: string,
    public reverseViewName: string,
    public options: DatelessLengthOption[]
  ) { }
}

export class DatelessLengthOption {
  constructor(public viewValue: string, public value: string) { }
}

export class DatelessSearchRequestItem {
  constructor(
    public size: string,
    public search: string,
    public callback: (res: DatelessSearchResponse) => void,
    public canReturn: boolean = true
  ) { }

  public pop(): void {
    this.canReturn = false;
  }
}

@Component({
  selector: 'pla-dateless-search-page',
  templateUrl: './dateless-search-page.component.html',
  styleUrls: ['./dateless-search-page.component.scss'],
})
export class DatelessSearchPageComponent implements OnInit, AfterViewInit {
  public npFeatured = new FeatureType(
    'Recently Added',
    'recently-added'
  );
  public datelessFeatured = new FeatureType('Featured Dateless', 'dateless');
  public results: DatelessSearchResponse = new DatelessSearchResponse(
    '',
    '',
    [],
  );
  public allResults: SearchReqResponse;
  public canSearch: boolean = false;
  public searching: boolean = false;
  public tableTitle: string;
  public all_resultChange: EventEmitter<SearchReqResponse> =
    new EventEmitter<SearchReqResponse>();

  private filter: string = '';
  public lastSearchTime: number = 0;
  public selectionCooldown: number = 1500;
  public queuedDebounce: boolean = false;

  public sortTrigger: EventEmitter<void> = new EventEmitter<void>();
  public mobileFilterToggle: EventEmitter<boolean> = new EventEmitter<boolean>();
  public searchTrigger: EventEmitter<void> = new EventEmitter<void>();
  public changeDropdown: EventEmitter<DropdownOption> = new EventEmitter<DropdownOption>();
  public selectedDropdownOption: DropdownOption;
  public datelessOptionsDropdownConfig: DropdownConfig = new DropdownConfig('Select a format', 'swap_vert', [
    new DropdownOption('*x-', 'One Letter and One Number', { styleName: '1x1', length: 2 }),
    new DropdownOption('*x--', 'One Letter and Two Numbers', { styleName: '1x2', length: 3 }),
    new DropdownOption('*x---', 'One Letter and Three Numbers', { styleName: '1x3', length: 4 }),
    new DropdownOption('*x----', 'One Letter and Four Numbers', { styleName: '1x4', length: 5 }),
    new DropdownOption('**x-', 'Two Letters and One Number', { styleName: '2x1', length: 3 }),
    new DropdownOption('**x--', 'Two Letters and Two Numbers', { styleName: '2x2', length: 4 }),
    new DropdownOption('**x---', 'Two Letters and Three Numbers', { styleName: '2x3', length: 5 }),
    new DropdownOption('**x----', 'Two Letters and Four Numbers', { styleName: '2x4', length: 6 }),
    new DropdownOption('***x-', 'Three Letters and One Number', { styleName: '3x1', length: 4 }),
    new DropdownOption('***x--', 'Three Letters and Two Numbers', { styleName: '3x2', length: 5 }),
    new DropdownOption('***x---', 'Three Letters and Three Numbers', { styleName: '3x3', length: 6 }),
    new DropdownOption('***x----', 'Three Letters and Four Numbers', { styleName: '3x4', length: 7 }),
  ], { showSelected: true, selectOnStart: true });

  public stickyLinkConfig: StickyConfig;
  private pageStyle: string;
  private searchCriteria: string;
  public termSorting: DropdownOption;
  public filterChange: EventEmitter<SearchFilters> = new EventEmitter<SearchFilters>();
  public filterUpdate: EventEmitter<SidebarFilterConfig<any>> =
    new EventEmitter<SidebarFilterConfig<any>>();
  public sortDropdownConfig: DropdownConfig = new DropdownConfig('Sort By', 'swap_vert', [
    new DropdownOption('A-Z', 'A-Z'),
    new DropdownOption('Z-A', 'Z-A'),
    new DropdownOption('LOW-TO-HIGH', 'Price Low to High'),
    new DropdownOption('HIGH-TO-LOW', 'Price High to Low')
  ]);
  public searchFilters: SidebarFilterConfig<SearchFilters> = new SidebarFilterConfig<SearchFilters>(
    [
      new SidebarFilterGroup('Sort By', [
        new SidebarFilter('sort', 'Sort By', 'sortBy', {
          selectConfig: this.sortDropdownConfig
        })
      ], { visible: true, customClass: 'd-block d-lg-none' }),
    ], this.filterChange);

  public faq: InfoDropdown[] = GlobalFaqs.GetFaqsByKey('Dateless');
  private debounceTimeout: any;  // Variable to store the timeout ID


  constructor(
    private searchService: SearchService,
    private registrationService: RegistrationService,
    private route: ActivatedRoute,
    private location: Location,
    private trackingService: TrackingService
  ) {
    this.pageStyle = this.route.snapshot.params.length ?? this.route.snapshot.params.format;
    this.searchCriteria = this.route.snapshot.params.searchCriteria ?? '';

    this.filterChange.subscribe((_: SearchFilters) => {
      this.TermSortChange(this.sortDropdownConfig.options.find(o => o.value === _.sortBy), "");
    });
    this.stickyLinkConfig = new StickyConfig(
      [
        new StickyLinkGroup([
          new StickyLink('Results', 'results', true),
          new StickyLink('Q&As', 'faq', false),
        ].filter(x => x != null)),
      ],
      new EventEmitter<void>()
    );

    // Show results when we have a plate type (layout); Preferred Letters/Numbers (searchCriteria) is optional
    if (this.pageStyle == null || this.pageStyle === undefined || this.pageStyle === '') {
      this.canSearch = false;
      return;
    }
    this.canSearch = true;

    this.trackingService.TrackSearch(
      TRACKING_SEARCH_TYPE.DATELESS,
      this.searchCriteria || '',
      {
        size: this.pageStyle,
      }
    );
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    if (this.pageStyle === null || this.pageStyle === undefined || this.pageStyle === '') this.pageStyle = '*x-';
    console.log('dateless aftver view - pageStyle', this.pageStyle);
    const toSelect = [...this.datelessOptionsDropdownConfig.options];
    const option = toSelect.find(x => x.value === this.pageStyle)
    if (option === undefined) return;
    this.changeDropdown.emit(option);
    this.OptionChange(option);
  }

  public OptionChange(e: DropdownOption): void {
    this.selectedDropdownOption = e;
    this.canSearch = true;
    this.tableTitle = e.metaData['styleName'];
    this.location.go(this.createUrl());
    this.makeSearchRequest();
  }

  public TermSortChange(e: DropdownOption, key: string): void {
    this.termSorting = e;
    if (this.allResults == undefined) return;
    if (e.value === 'A-Z') this.allResults.registrations = this.allResults.registrations.sort((a, b) => a.registration.localeCompare(b.registration));
    else if (e.value === 'Z-A') this.allResults.registrations = this.allResults.registrations.sort((a, b) => b.registration.localeCompare(a.registration));
    else if (e.value === 'HIGH-TO-LOW') this.allResults.registrations = this.allResults.registrations.sort((a, b) => b.price - a.price);
    else if (e.value === 'LOW-TO-HIGH') this.allResults.registrations = this.allResults.registrations.sort((a, b) => a.price - b.price);
    this.all_resultChange.emit({ ...this.allResults });
  }

  private createUrl(): string {
    // plates/dateless/{format} is the noindexed canonical form; dateless-search/* is legacy (301 at the edge).
    return `plates/dateless/${this.selectedDropdownOption.value}`;
  }

  public selectedOptionLength(): number {
    return this.selectedDropdownOption.metaData['length'];
  }

  public filterSearchResults(input: string): void {
    this.searchCriteria = input;

    clearTimeout(this.debounceTimeout);
    this.debounceTimeout = setTimeout(() => {
      this.makeSearchRequest();
    }, 500);
  }

  private makeSearchRequest(): void {
    this.searching = true;
    this.searchService.searchDatelessLength(
      this.selectedDropdownOption.value,
      this.searchCriteria || '',
      (res: DatelessSearchResponse) => {
        this.filterUpdate.emit(this.searchFilters);
        const main = res?.main;
        this.allResults = {
          id: 'dateless',
          registrations: Array.isArray(main) ? this.registrationService.formatRegistrations(main) : [],
        };
        if (this.termSorting) this.TermSortChange(this.termSorting, '');
        setTimeout(() => {
          this.searching = false;
        }, 500);
      }
    );
  }

  public searchTitle(header: string): string {
    return this.tableTitle;//`${this.results.style}  (${header})`;
  }
}
